import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FIXED_BASE } from "@/lib/ticketTypes";

export default function GenerateTickets() {
  const [type, setType] = useState("REGULAR");
  const [qty, setQty] = useState(10);
  const [drinks, setDrinks] = useState(1);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const locked = FIXED_BASE[type];

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tickets/generate", {
        ticket_type: type, quantity: Number(qty), drinks_allowed: Number(locked || drinks),
      });
      setLast(data);
      toast.success(`Generated ${data.created} tickets`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <div className="text-xs tracking-[0.3em] uppercase text-orange-400 font-bold">Bulk</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-white tracking-tight">GENERATE TICKETS</h1>
        <p className="text-slate-400 mt-2 text-sm">Each ticket gets a unique secure PDF417 barcode token.</p>
      </div>

      <div className="bg-[#131D33] border border-slate-800 rounded-2xl p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs tracking-widest uppercase text-slate-400">Ticket Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="bg-[#0B1120] border-slate-700 text-white" data-testid="generate-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="REGULAR">Regular</SelectItem>
                <SelectItem value="VIP">VIP</SelectItem>
                <SelectItem value="EMPLOYEE">Employee</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs tracking-widest uppercase text-slate-400">Quantity</Label>
            <Input type="number" min="1" max="2000" value={qty} onChange={(e) => setQty(e.target.value)} className="bg-[#0B1120] border-slate-700 text-white" data-testid="generate-qty" />
          </div>
          <div>
            <Label className="text-xs tracking-widest uppercase text-slate-400">Drinks per Ticket</Label>
            <Input type="number" min="1" max="50" value={locked || drinks} disabled={!!locked} title={locked ? "Base drinks are fixed for this ticket type" : undefined} onChange={(e) => setDrinks(e.target.value)} className="bg-[#0B1120] border-slate-700 text-white" data-testid="generate-drinks" />
          </div>
        </div>
        <Button
          onClick={submit} disabled={busy}
          className="w-full h-12 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-base rounded-xl"
          data-testid="generate-submit-btn"
        >
          {busy ? "Generating…" : "GENERATE TICKETS"}
        </Button>
      </div>

      {last && (
        <div className="bg-[#131D33] border border-slate-800 rounded-2xl p-6">
          <div className="text-emerald-400 font-bold mb-2">✓ Generated {last.created} tickets</div>
          <div className="text-slate-400 text-sm mb-3">First {Math.min(20, last.tickets.length)} shown. Manage from Tickets page.</div>
          <div className="max-h-72 overflow-y-auto space-y-1 font-mono-fb text-sm">
            {last.tickets.slice(0, 20).map((t) => (
              <div key={t.id} className="flex justify-between text-slate-300">
                <span className="text-orange-300">{t.ticket_id}</span>
                <span className="text-slate-500 truncate ml-3">{t.barcode_token}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
